import { Component, Element, Host, h, Prop, State, Listen } from '@stencil/core';
import { getCurrentMediaQuery } from '../../../utils/getCurrentMediaQuery';

interface Tips {
    fs_date: number;
    tips: Tip[];
}

interface Tip {
    start: number;
    end: number;
    button_text: string;
    button_link: string;
    headline: string;
    image_alt: string;
    image_src: string;
    image_title: string;
    link_target: string;
    text: string;
    title: string;
    pages: string[];
    id: number;
}

@Component({
    tag: 'sew-ws-floating-buttons',
    styleUrl: 'sew-ws-floating-buttons.scss',
    shadow: true,
})
export class SewWsFloatingButtons {
    @Element() host: HTMLSewWsFloatingButtonsElement;

    @Prop() cardTitle: string;
    @Prop() firstPhoneTitle: string;
    @Prop() firstPhoneNumber: string;
    @Prop() firstPhoneHelpText: string;
    @Prop() secondPhoneTitle: string;
    @Prop() secondPhoneNumber: string;
    @Prop() secondPhoneHelpText: string;
    @Prop() thirdPhoneTitle: string;
    @Prop() thirdPhoneNumber: string;
    @Prop() thirdPhoneHelpText: string;
    @Prop() chatBtnTitle: string;
    @Prop() chatBtnText: string;
    @Prop() linkBtnText: string;
    @Prop() linkBtnLink: string;
    @Prop() linkBtnIcon: string;
    @Prop() apiKey: string;
    @Prop() localization = { region: 'de', language: 'de' };
    @Prop() apiBaseUrl: string;
    @Prop() externalZipcodeSearch: string;
    @Prop() externalZipcodeInputName: string;
    @Prop() placeholder: string;
    @Prop() tipOptOutLabel: string;
    @Prop() tipDuration: number;
    @Prop() tipApiUrl: string;
    @Prop() tipLabel: string;
    @Prop() enableDownloads: boolean;
    @Prop() enableWhatsapp: boolean;
    @Prop() enableTips: boolean;
    @Prop() enableLinkButton: boolean;
    @Prop() scrollToTopAriaLabel = 'Scroll To Top';
    @Prop() contactAriaLabel = 'Contact';
    @Prop() downloadAriaLabel = 'Download';
    @Prop() whatsappAriaLabel = 'WhatsApp';
    @Prop() enableIdleAnimation: boolean;

    @State() scrolled = false;
    @State() openFlyout: 'contact' | 'tip' | 'download' | 'whatsapp' | null = null;
    @State() isScrollToTopVisible = false;
    @State() tips: Tips;
    @State() currentTip: Tip;
    @State() idle = false;
    @State() loadGoogleApi = false;
    @State() webchatChecked = false;
    @State() webchatInitialized = false;

    private lastScrollPosition = 0;
    private hasBeenShown = false;
    private floatingButtonsRef: HTMLDivElement;
    private multiButtonRef: HTMLSewWsMultiButtonElement;
    private scrollTimeout: any;
    private initCheckInterval;
    private webchatToggleBtnRef: HTMLButtonElement;

    @Listen('scroll', { target: 'window' })
    onScroll(): void {
        if (this.enableIdleAnimation) {
            clearTimeout(this.scrollTimeout);
            this.idle = false;
            this.scrollTimeout = setTimeout(() => {
                this.idle = true;
            }, 3500);
        }

        if (window.scrollY > 0) {
            this.scrolled = true;

            if (this.lastScrollPosition > window.scrollY) {
                this.isScrollToTopVisible = true;
            } else {
                this.isScrollToTopVisible = false;
            }
        } else {
            this.scrolled = false;
            this.isScrollToTopVisible = false;
        }

        this.lastScrollPosition = window.scrollY;
    }

    @Listen('resize', { target: 'window' })
    onResize(): void {
        this.host.style.setProperty('--current-client-width', document.body.clientWidth + 'px');
    }

    /**
     * Tracks if any Element outside of the Contact Flyout was clicked.
     * If this is the case, close the Contact Flyout closes.
     * @param eventWindowClicked Event emitted on click on window
     */
    @Listen('mousedown', { target: 'window' })
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    checkForClickOutside(eventWindowClicked): void {
        const path =
            eventWindowClicked.path || (eventWindowClicked.composedPath && eventWindowClicked.composedPath());

        if (this.openFlyout && path.every(el => el !== this.host)) {
            this.toggleOpenFloatingCard();
        }
    }

    private toggleOpenFloatingCard = (flyout?: 'contact' | 'tip' | 'download' | 'whatsapp'): void => {
        if (flyout) {
            this.loadGoogleApi = true;
            this.calcMultiButtonPosition();
            this.openFlyout = flyout;
        } else {
            this.openFlyout = null;
        }

        if (getCurrentMediaQuery() === 1 && this.openFlyout) {
            document.body.classList.add('sew-modal-open');
        } else if (getCurrentMediaQuery() === 1 && !this.openFlyout) {
            document.body.classList.remove('sew-modal-open');
        }
    };

    private scrollToTop = (): void => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    private optOutInitialTipAnimation = (): void => {
        localStorage.setItem('optOutHighlight', 'true');
        this.toggleOpenFloatingCard();
        this.host.style.setProperty('--floating-buttons-height', this.floatingButtonsRef.clientHeight + 'px');
    };

    private optOutCurrentTipPopup = (): void => {
        let storageValue = localStorage.getItem('optOutTipPopup');

        if (!storageValue?.includes(String(this.currentTip.id))) {
            storageValue = storageValue
                ? storageValue + ' ,' + this.currentTip.id
                : String(this.currentTip.id);
            localStorage.setItem('optOutTipPopup', storageValue);
        }

        this.toggleOpenFloatingCard();
    };

    private fetchTips = async () => {
        await fetch(this.tipApiUrl)
            .then(res => res.json())
            .then(
                data => (this.tips = data._embedded['rh:doc'][0]),
                err => console.error(err)
            )
            .then(this.filterTips)
            .catch(err => console.error(err));
    };

    private checkTipUrl = (page: string): boolean => {
        const pagePathname = page.split('?')[0];
        const pageParams = page.split('?')[1];
        let matched = false;

        if (window.location.href.toLowerCase().includes(pagePathname.toLowerCase())) {
            if (pageParams) {
                matched =
                    pageParams
                        .split('&')
                        .filter(param => window.location.search.toLowerCase().includes(param.toLowerCase()))
                        .length === pageParams.split('&').length;
            } else {
                matched = true;
            }
        }

        return matched;
    };

    private filterTips = () => {
        this.currentTip = this.tips?.tips
            .map(tip => tip)
            .filter(
                tip =>
                    this.tips.fs_date > tip.start &&
                    this.tips.fs_date < tip.end &&
                    tip.pages.filter(page => this.checkTipUrl(page)).length > 0
            )[0];
    };

    componentWillLoad(): void {
        document.addEventListener('openContactButton', () => {
            this.toggleOpenFloatingCard('contact');
        });

        this.enableTips && this.tipApiUrl && this.fetchTips();

        if (!sessionStorage.getItem('sew-floating-buttons')) {
            sessionStorage.setItem('sew-floating-buttons', 'true');
        } else {
            this.hasBeenShown = true;
        }

        if (!document.querySelector('sew-ws-stage:not([compact])')) {
            this.host.classList.add('no-stage');
        }

        const linklistLength = this.host.querySelectorAll('sew-ws-link[slot="links"]').length;
        // link height times number and gap height times number - 1
        if (linklistLength) {
            const linklistHeight = linklistLength * 24 + (linklistLength - 1) * 12;
            this.host.style.setProperty('--flyout-offset', linklistHeight + 'px');
        }
    }

    private tipInitialTeaser = (duration: number): void => {
        setTimeout(() => this.toggleOpenFloatingCard('tip'), 1000);
        setTimeout(() => this.toggleOpenFloatingCard(), duration);
    };

    private calcMultiButtonPosition = () => {
        if (!this.multiButtonRef) return;

        const position =
            window.innerHeight -
            this.multiButtonRef.getBoundingClientRect().bottom +
            this.multiButtonRef.clientHeight;

        this.host.style.setProperty('--multi-button-position', position + 'px');
    };

    private getConsent(): boolean {
        return document.cookie.indexOf('googlemaps-opt-in=true') !== -1;
    }

    private checkChatInitialization = () => {
        const chatContainer = document.querySelector('.webchat-toggle-button');
        if (chatContainer) {
            this.webchatToggleBtnRef = chatContainer as HTMLButtonElement;
            this.webchatInitialized = true;
            clearInterval(this.initCheckInterval);
        }
        this.webchatChecked = true;
    };

    componentDidLoad(): void {
        this.host.style.setProperty('--current-client-width', document.body.clientWidth + 'px');

        if (
            this.enableTips &&
            this.currentTip &&
            !localStorage.getItem('optOutHighlight') &&
            !localStorage.getItem('optOutTipPopup')?.includes(String(this.currentTip.id))
        ) {
            this.hasBeenShown = true;
            this.tipInitialTeaser(this.tipDuration);
        }

        setInterval(this.checkChatInitialization, 500);
    }

    componentDidRender(): void {
        this.host.style.setProperty('--floating-buttons-height', this.floatingButtonsRef.clientHeight + 'px');
    }

    public render() {
        return (
            <Host
                class={{
                    'scrolled': this.scrolled,
                    'scroll-to-top-visible': this.isScrollToTopVisible,
                    'idle': this.enableIdleAnimation && this.idle && !this.openFlyout,
                    'flyout-open': !!this.openFlyout,
                }}
            >
                <div
                    class={{
                        'flyout-layer': true,
                        'contact-flyout': true,
                        'multi-button': this.enableDownloads,
                        'open': this.openFlyout === 'contact',
                    }}
                >
                    <sew-icon onClick={() => this.toggleOpenFloatingCard()} class="close-icon" icon="close" />
                    <h3 class="card-title" innerHTML={this.cardTitle}></h3>
                    <div class="card-content">
                        <div class="headline">{this.firstPhoneTitle}</div>
                        <a href={'tel:' + this.firstPhoneNumber} class="text">
                            {this.firstPhoneNumber}
                        </a>
                        {this.firstPhoneHelpText && <div class="help-text">{this.firstPhoneHelpText}</div>}
                    </div>
                    <hr />
                    <div class="card-content">
                        <div class="headline">{this.secondPhoneTitle}</div>
                        <a href={'tel:' + this.secondPhoneNumber} class="text">
                            {this.secondPhoneNumber}
                        </a>
                        {this.secondPhoneHelpText && <div class="help-text">{this.secondPhoneHelpText}</div>}
                    </div>
                    {this.thirdPhoneTitle && <hr />}
                    {this.thirdPhoneTitle && (
                        <div class="card-content">
                            <div class="headline">{this.thirdPhoneTitle}</div>
                            <a href={'tel:' + this.thirdPhoneNumber} class="text">
                                {this.thirdPhoneNumber}
                            </a>
                            {this.thirdPhoneHelpText && (
                                <div class="help-text">{this.thirdPhoneHelpText}</div>
                            )}
                        </div>
                    )}
                    {!this.externalZipcodeSearch && this.getConsent() && this.loadGoogleApi && (
                        <sew-search-field-google-api
                            class="search-field"
                            apiKey={this.apiKey}
                            localization={this.localization}
                            apiBaseUrl={this.apiBaseUrl}
                            placeholder={this.placeholder}
                            isFixed
                        ></sew-search-field-google-api>
                    )}
                    {this.externalZipcodeSearch && !this.getConsent() && (
                        <form action={this.externalZipcodeSearch}>
                            <sew-search-field class="search-field" ws>
                                <input
                                    type="text"
                                    name={this.externalZipcodeInputName}
                                    placeholder={this.placeholder}
                                />
                            </sew-search-field>
                        </form>
                    )}
                    {this.externalZipcodeSearch && this.getConsent() && this.loadGoogleApi && (
                        <sew-search-field-google-api
                            class="search-field"
                            apiKey={this.apiKey}
                            localization={this.localization}
                            externalZipcodeSearch={this.externalZipcodeSearch}
                            externalZipcodeInputName={this.externalZipcodeInputName}
                            placeholder={this.placeholder}
                            isFixed
                        ></sew-search-field-google-api>
                    )}
                    <sew-ws-linklist>
                        <slot name="links"></slot>
                    </sew-ws-linklist>
                </div>
                {this.enableTips && this.currentTip && (
                    <div
                        class={{
                            'flyout-layer': true,
                            'tip-flyout': true,
                            'open': this.openFlyout === 'tip',
                        }}
                    >
                        <sew-icon
                            onClick={() => this.optOutCurrentTipPopup()}
                            class="close-icon"
                            icon="close"
                        />
                        <h3 class="highlight-title">{this.currentTip.title}</h3>
                        <img
                            loading="lazy"
                            src={this.currentTip.image_src}
                            alt={this.currentTip.image_alt}
                            title={this.currentTip.image_title}
                        />
                        <p class="highlight-headline">{this.currentTip.headline}</p>
                        <p class="highlight-text">{this.currentTip.text}</p>
                        <sew-ws-button class="tip-btn">
                            <a href={this.currentTip.button_link} target={this.currentTip.link_target}>
                                {this.currentTip.button_text}
                            </a>
                        </sew-ws-button>
                        <sew-ws-link class="tip-opt-out-link">
                            <button onClick={this.optOutInitialTipAnimation}>{this.tipOptOutLabel}</button>
                        </sew-ws-link>
                    </div>
                )}

                {this.enableDownloads && (
                    <div
                        class={{
                            'flyout-layer': true,
                            'download-flyout': true,
                            'multi-button': true,
                            'open': this.openFlyout === 'download',
                        }}
                    >
                        <sew-icon
                            onClick={() => this.toggleOpenFloatingCard()}
                            class="close-icon"
                            icon="close"
                        />
                        <slot name="download"></slot>
                    </div>
                )}

                {this.enableWhatsapp && (
                    <div
                        class={{
                            'flyout-layer': true,
                            'whatsapp-flyout': true,
                            'multi-button': true,
                            'open': this.openFlyout === 'whatsapp',
                            'second': !this.enableDownloads && this.enableWhatsapp,
                            'third': this.enableDownloads && this.enableWhatsapp,
                        }}
                    >
                        <sew-icon
                            onClick={() => this.toggleOpenFloatingCard()}
                            class="close-icon"
                            icon="close"
                        />
                        <slot name="whatsapp"></slot>
                    </div>
                )}

                <div
                    class="floating-buttons"
                    ref={el => (this.floatingButtonsRef = el)}
                    onTransitionEnd={() => this.calcMultiButtonPosition()}
                >
                    {this.webchatInitialized && (
                        <button
                            class="chat-btn"
                            aria-label="webchat"
                            onClick={() => this.webchatToggleBtnRef.click()}
                        >
                            <div class="hover">
                                <div class="title">{this.chatBtnTitle}</div>
                                <div class="text">{this.chatBtnText}</div>
                            </div>
                            <div class="icon">
                                <sew-icon class="svg" icon="chat-bubbles" size="xl" />
                            </div>
                        </button>
                    )}
                    {this.enableLinkButton && (
                        <a class="link-btn" aria-label={this.linkBtnText} href={this.linkBtnLink}>
                            <div class="hover">
                                <div class="text">{this.linkBtnText}</div>
                            </div>
                            <div class="icon">
                                <sew-icon class="svg" icon={this.linkBtnIcon} size="xl" />
                            </div>
                        </a>
                    )}
                    {this.enableTips && this.currentTip && (
                        <sew-ws-tip-button
                            onClickedButton={e => this.toggleOpenFloatingCard(e.detail)}
                            tip-label={this.tipLabel}
                            initial-animation={!this.hasBeenShown}
                        />
                    )}

                    {this.enableDownloads || this.enableWhatsapp ? (
                        <sew-ws-multi-button
                            ref={el => (this.multiButtonRef = el)}
                            onClickedButton={e => this.toggleOpenFloatingCard(e.detail)}
                            expanded={
                                this.openFlyout === 'contact' ||
                                this.openFlyout === 'download' ||
                                this.openFlyout === 'whatsapp'
                            }
                            initial-animation={!this.hasBeenShown}
                            whatsapp-aria-label={this.enableWhatsapp && this.whatsappAriaLabel}
                            download-aria-label={this.enableDownloads && this.downloadAriaLabel}
                            contact-aria-label={this.contactAriaLabel}
                        />
                    ) : (
                        <button
                            class={{ 'contact-btn': true, 'visible': this.webchatChecked }}
                            onClick={() => this.toggleOpenFloatingCard('contact')}
                            aria-label={this.cardTitle}
                        >
                            <div class={{ 'expanded': true, 'has-been-shown': this.hasBeenShown }}>
                                <span class="title">{this.firstPhoneTitle}</span>
                                <span class="phone">{this.firstPhoneNumber}</span>
                            </div>
                            <div class="icon">
                                <sew-icon class="svg" icon="mail" size="xl" />
                            </div>
                        </button>
                    )}

                    <button
                        class="scroll-to-top-btn"
                        onClick={this.scrollToTop}
                        aria-label={this.scrollToTopAriaLabel}
                    >
                        <sew-icon class="svg" icon="arrow-up" size="xl" />
                    </button>
                </div>
            </Host>
        );
    }
}
